import React, { Fragment, useEffect, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Select,
  MenuItem,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAllCustomers } from "../../reduxToolkit/features/auth";
import { getAllOrders } from "../../reduxToolkit/features/frontend/checkout";
import { getCurrency } from "../../reduxToolkit/features/adminPanel/misc";
import getSymbolFromCurrency from "currency-symbol-map";

import { axiosInstance } from "../../config";

// Media Query
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import LoadingBox from "../../components/misc/Loading";
import DashboardCard from "../../components/adminPanel/shared/DashboardCard";
import EmptyStateAlert from "../../components/adminPanel/shared/EmptyStateAlert";
import LatestCustomers from "./LatestCustomers";
import LatestOrders from "./LatestOrders";
import LatestProducts from "./LatestProducts";
import LatestChangesMade from "./LatestChangesMade";
import KnownIssues from "./KnownIssues";
import ChangeLog from "./ChangeLog";
import { ExpandMore } from "@mui/icons-material";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import { colors, fonts } from "../../theme/designTokens";

// Time-range options for the dashboard charts; "all" resolves to the
// actual span of the data (oldest record -> today) at render time.
const CHART_RANGE_OPTIONS = [
  { value: 7, label: "7D" },
  { value: 30, label: "30D" },
  { value: 90, label: "90D" },
  { value: 365, label: "1Y" },
  { value: "all", label: "All Time" },
];

// How many calendar days back the oldest `dateField` value in `items` is,
// used to resolve the "all" range option. Falls back to 14 for empty data.
const getDaysSpan = (items, dateField) => {
  if (!Array.isArray(items) || items.length === 0) return 14;
  let minTime = Date.now();
  items.forEach((item) => {
    const t = item[dateField] ? new Date(item[dateField]).getTime() : null;
    if (t && !Number.isNaN(t) && t < minTime) minTime = t;
  });
  return Math.max(Math.ceil((Date.now() - minTime) / 86400000) + 1, 1);
};

// Builds the last `days` worth of buckets (oldest first), then fills them
// from `items` keyed by `dateField` (a date string field). Buckets by day
// for short ranges, week for medium ranges, and month for long/"all time"
// ranges, so the chart stays readable instead of showing hundreds of bars.
const buildSeries = (items, dateField, days, extra) => {
  const safeItems = Array.isArray(items) ? items : [];
  const unit = days <= 60 ? "day" : days <= 730 ? "week" : "month";
  const today = new Date();

  const weekStart = (d) => {
    const dt = new Date(d);
    const dow = dt.getDay() || 7;
    dt.setDate(dt.getDate() - dow + 1);
    return dt;
  };

  const keyOf = (d) => {
    if (unit === "day") return d.toISOString().slice(0, 10);
    if (unit === "week") return weekStart(d).toISOString().slice(0, 10);
    return d.toISOString().slice(0, 7);
  };

  const labelOf = (d) => {
    if (unit === "day")
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (unit === "week")
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };

  const buckets = [];
  if (unit === "month") {
    const totalMonths = Math.ceil(days / 30);
    for (let i = totalMonths - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      buckets.push({ key: keyOf(d), label: labelOf(d), count: 0, revenue: 0 });
    }
  } else {
    const stepDays = unit === "day" ? 1 : 7;
    const totalSteps = Math.ceil(days / stepDays);
    for (let i = totalSteps - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i * stepDays);
      buckets.push({ key: keyOf(d), label: labelOf(d), count: 0, revenue: 0 });
    }
  }

  const byKey = {};
  buckets.forEach((b) => (byKey[b.key] = b));
  safeItems.forEach((item) => {
    if (!item[dateField]) return;
    const d = new Date(item[dateField]);
    if (Number.isNaN(d.getTime())) return;
    const key = keyOf(d);
    if (byKey[key]) {
      byKey[key].count += 1;
      if (extra) byKey[key].revenue += extra(item);
    }
  });
  return buckets;
};

const getOrderTotal = (order) => {
  if (!order.orderedItems || !order.orderedItems.length) return 0;
  return order.orderedItems.reduce(
    (sum, c) => sum + Number(c.price || 0) * Number(c.moq || 0),
    0
  );
};

// Revenue is stored in the site's base currency; convert with the same
// currencyList/exchangeRate data already used by Price Info elsewhere in
// the admin panel, then format with that currency's symbol.
const formatMoney = (amount, currencyCode) =>
  `${getSymbolFromCurrency(currencyCode) || ""}${Number(
    amount || 0
  ).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function AdminHome() {
  const dispatch = useDispatch();

  // Media Query
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [loadingMain, setLoadingMain] = useState(false);

  const [latestCustomerList, setLatestCustomerList] = useState([]);

  const [adminUrl, setAdminUrl] = useState("");

  // Store
  const { customers } = useSelector((state) => state.auth);
  const { orders } = useSelector((state) => state.checkout);
  const { currencyList } = useSelector((state) => state.misc);

  // Revenue currency (defaults to the base currency the stored amounts are in)
  const [revenueCurrency, setRevenueCurrency] = useState("USD");
  const revenueRate = useMemo(() => {
    const C = currencyList.find((c) => c.currencyName === revenueCurrency);
    return C ? C.exchangeRate : 1;
  }, [currencyList, revenueCurrency]);

  // Functions
  const getAdminUrl = async () => {
    const res = await axiosInstance.get("/api/adminUrl");
    setAdminUrl(res.data.url);
  };

  // UseEffect
  useEffect(() => {
    getAdminUrl();
    setLoadingMain(true);
    if (!(customers && customers.length > 0)) dispatch(getAllCustomers());
    if (!(orders && orders.length > 0)) dispatch(getAllOrders());
    if (!(currencyList && currencyList.length > 0)) dispatch(getCurrency());
    setTimeout(() => {
      setLoadingMain(false);
    }, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (customers && customers.length > 0) {
      setLatestCustomerList(customers.slice(0, mdUp ? 10 : 5));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  // Chart time ranges (each chart picks its own)
  const [ordersRange, setOrdersRange] = useState("all");
  const [customersRange, setCustomersRange] = useState("all");

  const ordersChartData = useMemo(() => {
    const days =
      ordersRange === "all" ? getDaysSpan(orders, "createdAt") : ordersRange;
    return buildSeries(orders, "createdAt", days, getOrderTotal);
  }, [orders, ordersRange]);

  const customersChartData = useMemo(() => {
    const days =
      customersRange === "all"
        ? getDaysSpan(customers, "createdAt")
        : customersRange;
    return buildSeries(customers, "createdAt", days);
  }, [customers, customersRange]);

  // Totals for the currently selected range, derived from the same
  // buckets the chart renders (so they always add up to the bars shown).
  const ordersTotals = useMemo(
    () =>
      ordersChartData.reduce(
        (acc, b) => ({
          count: acc.count + b.count,
          revenue: acc.revenue + b.revenue,
        }),
        { count: 0, revenue: 0 }
      ),
    [ordersChartData]
  );
  const customersTotal = useMemo(
    () => customersChartData.reduce((sum, b) => sum + b.count, 0),
    [customersChartData]
  );

  // Orders chart data with revenue converted to the selected display currency
  const ordersChartDisplay = useMemo(
    () =>
      ordersChartData.map((b) => ({ ...b, revenue: b.revenue * revenueRate })),
    [ordersChartData, revenueRate]
  );

  //! Translate
  // Store
  const { langArrFromClientSide } = useSelector((state) => state.misc);
  const translate0 = (String) => {
    let translation = "";
    if (langArrFromClientSide && langArrFromClientSide.length > 0) {
      const T = langArrFromClientSide.find((l) => l.title === String);
      if (T && T.value) {
        let obj = { ...T };
        if (T.value === "") obj.value = T.title;
        translation = T.value;
      } else if (T === undefined) translation = String;
    }
    return translation || String;
  };
  //! Translate

  return (
    <Fragment>
      {loadingMain ? (
        <LoadingBox w={"100%"} h={"90vh"} />
      ) : (
        <Grid
          container
          spacing={1}
          alignItems="stretch"
          sx={{
            width: "100%", minWidth: 0,
          }}
        >
          <Grid item xs={12} md={7}>
            <DashboardCard
              title={translate0("Orders & Revenue")}
              minHeight={300}
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={ordersRange}
                      onChange={(e) => setOrdersRange(e.target.value)}
                    >
                      {CHART_RANGE_OPTIONS.map((o) => (
                        <MenuItem key={o.label} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {currencyList && currencyList.length > 0 && (
                    <Autocomplete
                      size="small"
                      disableClearable
                      options={currencyList}
                      value={
                        currencyList.find(
                          (c) => c.currencyName === revenueCurrency
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        newValue && setRevenueCurrency(newValue.currencyName)
                      }
                      getOptionLabel={(option) => option.currencyName || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.currencyName === value.currencyName
                      }
                      sx={{ minWidth: 110 }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                </Box>
              }
            >
              <Grid container spacing={2} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: colors.ink2, fontWeight: 700, textTransform: "uppercase" }}>
                    {translate0("Orders")}
                  </Typography>
                  <Typography sx={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 22, color: colors.ink }}>
                    {ordersTotals.count}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: colors.ink2, fontWeight: 700, textTransform: "uppercase" }}>
                    {translate0("Revenue")}
                  </Typography>
                  <Typography sx={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 22, color: colors.accent }}>
                    {formatMoney(ordersTotals.revenue * revenueRate, revenueCurrency)}
                  </Typography>
                </Grid>
              </Grid>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={ordersChartDisplay} margin={{ left: -12 }}>
                  <CartesianGrid stroke={colors.line} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: colors.ink2, fontFamily: fonts.body }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11, fill: colors.ink2, fontFamily: fonts.body }}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11, fill: colors.ink2, fontFamily: fonts.body }}
                    tickFormatter={(v) => `${getSymbolFromCurrency(revenueCurrency) || ""}${v}`}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${colors.line}`,
                      fontFamily: fonts.body,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: colors.ink, fontWeight: 700 }}
                    itemStyle={{ color: colors.ink }}
                    formatter={(value, name) =>
                      name === translate0("Revenue")
                        ? formatMoney(value, revenueCurrency)
                        : value
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 12, fontFamily: fonts.body, color: colors.ink }} />
                  <Bar
                    yAxisId="left"
                    dataKey="count"
                    name={translate0("Orders")}
                    fill={colors.primaryLight}
                    stroke={colors.primary}
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    name={translate0("Revenue")}
                    stroke={colors.accent}
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </DashboardCard>
          </Grid>
          <Grid item xs={12} md={5}>
            <DashboardCard
              title={translate0("New Customers")}
              minHeight={300}
              action={
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select
                    value={customersRange}
                    onChange={(e) => setCustomersRange(e.target.value)}
                  >
                    {CHART_RANGE_OPTIONS.map((o) => (
                      <MenuItem key={o.label} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
            >
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 11, color: colors.ink2, fontWeight: 700, textTransform: "uppercase" }}>
                  {translate0("New Customers")}
                </Typography>
                <Typography sx={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 22, color: colors.ink }}>
                  {customersTotal}
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={customersChartData} margin={{ left: -12 }}>
                  <CartesianGrid stroke={colors.line} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: colors.ink2, fontFamily: fonts.body }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: colors.ink2, fontFamily: fonts.body }}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${colors.line}`,
                      fontFamily: fonts.body,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: colors.ink, fontWeight: 700 }}
                    itemStyle={{ color: colors.ink }}
                  />
                  <Bar
                    dataKey="count"
                    name={translate0("New Customers")}
                    fill={colors.accentLight}
                    stroke={colors.accent}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </DashboardCard>
          </Grid>
          <Grid item xs={12} sm={6} md={6} xl={6}>
            <DashboardCard title={translate0("Latest Users")} minHeight={500}>
              {latestCustomerList.length < 1 ? (
                <EmptyStateAlert message={translate0("No Data Available")} />
              ) : (
                <LatestCustomers
                  latestCustomerList={latestCustomerList}
                  adminUrl={adminUrl}
                />
              )}
            </DashboardCard>
          </Grid>
          <Grid item xs={12} sm={6} md={6} xl={6}>
            <DashboardCard title={translate0("Latest Orders")}>
              <LatestOrders />
            </DashboardCard>
          </Grid>
          <Grid item xs={12} sm={6} md={6} xl={6}>
            <DashboardCard title={translate0("Latest Products Added")}>
              <LatestProducts />
            </DashboardCard>
          </Grid>

          <Grid item xs={12} sm={6} md={6} xl={6}>
            <DashboardCard title={translate0("Latest Changes Made")}>
              <LatestChangesMade />
            </DashboardCard>
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={6}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-label="Expand"
                aria-controls="known-issue-content"
                id="known-issue-header"
              >
                <Typography>{translate0("Known Issue")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <KnownIssues />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={6}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-label="Expand"
                aria-controls="chagne-log-content"
                id="change-log-header"
              >
                <Typography>{translate0("Change Log")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ChangeLog />
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
}
